/**
 * Zustand store — gestión de estado global.
 *
 * Decisión: Zustand sobre Redux.
 * - Sin boilerplate (no reducers, no actions separately)
 * - Slices por dominio usando el patrón de slice functions
 * - Suscripciones granulares: el componente solo re-renderiza si
 *   el slice que consume cambia
 *
 * Para ejecuciones concurrentes: Map<run_id, ExecutionRun>
 * Cada run tiene su propia entrada independiente en el Map.
 * El selector WebSocket actualiza solo el run afectado.
 */
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import type {
  ExecutionPlan, ExecutionRun, StepResult,
  TestSystem, TestCase, Agent, DashboardMetrics, WSEvent,
} from '../types';

// ─── Execution Slice ──────────────────────────────────────────────────────────

interface ExecutionSlice {
  // Map run_id → run state (soporta hasta 20 concurrentes)
  activeRuns: Map<string, ExecutionRun>;
  plans: ExecutionPlan[];
  selectedPlanId: string | null;

  // Actions
  startRun: (run: ExecutionRun) => void;
  updateRun: (run_id: string, patch: Partial<ExecutionRun>) => void;
  appendStepResult: (run_id: string, step: StepResult) => void;
  completeRun: (run_id: string, final: Partial<ExecutionRun>) => void;
  setPlans: (plans: ExecutionPlan[]) => void;
  selectPlan: (id: string | null) => void;
  clearRun: (run_id: string) => void;
}

// ─── Cases Slice ─────────────────────────────────────────────────────────────

interface CasesSlice {
  systems: TestSystem[];
  selectedSystemId: string | null;
  cases: TestCase[];
  selectedCaseId: string | null;
  isAIGenerating: boolean;

  setSystems: (systems: TestSystem[]) => void;
  selectSystem: (id: string | null) => void;
  setCases: (cases: TestCase[]) => void;
  selectCase: (id: string | null) => void;
  updateCase: (id: string, patch: Partial<TestCase>) => void;
  setAIGenerating: (v: boolean) => void;
}

// ─── Agent Slice ──────────────────────────────────────────────────────────────

interface AgentSlice {
  agents: Map<string, Agent>;
  updateAgent: (agent: Agent) => void;
  removeAgent: (id: string) => void;
}

// ─── UI Slice ─────────────────────────────────────────────────────────────────

interface UISlice {
  sidebarCollapsed: boolean;
  activeSection: 'dashboard' | 'systems' | 'cases' | 'executions' | 'agents' | 'settings';
  notifications: Notification[];
  currentUserRole: string;

  toggleSidebar: () => void;
  setSection: (section: UISlice['activeSection']) => void;
  addNotification: (n: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  setCurrentUserRole: (role: string) => void;
}

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
}

// ─── Metrics Slice ────────────────────────────────────────────────────────────

interface MetricsSlice {
  metrics: DashboardMetrics | null;
  metricsLoading: boolean;
  setMetrics: (m: DashboardMetrics) => void;
  setMetricsLoading: (v: boolean) => void;
}

// ─── Combined Store ───────────────────────────────────────────────────────────

type AppStore = ExecutionSlice & CasesSlice & AgentSlice & UISlice & MetricsSlice;

export const useAppStore = create<AppStore>()(
  devtools(
    subscribeWithSelector((set, get) => ({

      // ── Execution ──────────────────────────────────────────────────────────
      activeRuns: new Map(),
      plans: [],
      selectedPlanId: null,

      startRun: (run) =>
        set((state) => {
          const next = new Map(state.activeRuns);
          next.set(run.id, { ...run, step_results: [] });
          return { activeRuns: next };
        }),

      updateRun: (run_id, patch) =>
        set((state) => {
          const run = state.activeRuns.get(run_id);
          if (!run) return {};
          const next = new Map(state.activeRuns);
          next.set(run_id, { ...run, ...patch });
          return { activeRuns: next };
        }),

      appendStepResult: (run_id, step) =>
        set((state) => {
          const run = state.activeRuns.get(run_id);
          if (!run) return {};
          const next = new Map(state.activeRuns);
          next.set(run_id, {
            ...run,
            step_results: [...(run.step_results ?? []), step],
            passed_steps: run.passed_steps + (step.status === 'passed' ? 1 : 0),
            failed_steps: run.failed_steps + (step.status === 'failed' ? 1 : 0),
          });
          return { activeRuns: next };
        }),

      completeRun: (run_id, final) =>
        set((state) => {
          const run = state.activeRuns.get(run_id);
          if (!run) return {};
          const next = new Map(state.activeRuns);
          next.set(run_id, { ...run, ...final });
          return { activeRuns: next };
        }),

      clearRun: (run_id) =>
        set((state) => {
          const next = new Map(state.activeRuns);
          next.delete(run_id);
          return { activeRuns: next };
        }),

      setPlans: (plans) => set({ plans }),
      selectPlan: (id) => set({ selectedPlanId: id }),

      // ── Cases ──────────────────────────────────────────────────────────────
      systems: [],
      selectedSystemId: null,
      cases: [],
      selectedCaseId: null,
      isAIGenerating: false,

      setSystems: (systems) => set({ systems }),
      selectSystem: (id) => set({ selectedSystemId: id }),
      setCases: (cases) => set({ cases }),
      selectCase: (id) => set({ selectedCaseId: id }),
      updateCase: (id, patch) =>
        set((state) => ({
          cases: state.cases.map((c) => (c.id === id ? { ...c, ...patch } : c)),
        })),
      setAIGenerating: (v) => set({ isAIGenerating: v }),

      // ── Agents ─────────────────────────────────────────────────────────────
      agents: new Map(),
      updateAgent: (agent) =>
        set((state) => {
          const next = new Map(state.agents);
          next.set(agent.id, agent);
          return { agents: next };
        }),
      removeAgent: (id) =>
        set((state) => {
          const next = new Map(state.agents);
          next.delete(id);
          return { agents: next };
        }),

      // ── UI ─────────────────────────────────────────────────────────────────
      sidebarCollapsed: false,
      activeSection: 'dashboard',
      notifications: [],
      currentUserRole: localStorage.getItem('user_role') ?? 'viewer',

      toggleSidebar: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSection: (section) => set({ activeSection: section }),
      setCurrentUserRole: (role) => set({ currentUserRole: role }),
      addNotification: (n) =>
        set((state) => ({
          notifications: [
            ...state.notifications,
            { ...n, id: crypto.randomUUID() },
          ],
        })),
      removeNotification: (id) =>
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        })),

      // ── Metrics ────────────────────────────────────────────────────────────
      metrics: null,
      metricsLoading: false,
      setMetrics: (m) => set({ metrics: m }),
      setMetricsLoading: (v) => set({ metricsLoading: v }),
    })),
    { name: 'qa-platform' }
  )
);

// ─── WebSocket event handler — conecta WS con el store ───────────────────────
export function handleWSEvent(event: WSEvent, store: typeof useAppStore) {
  const { startRun, updateRun, appendStepResult, completeRun, updateAgent } =
    store.getState();

  switch (event.event) {
    case 'run_started':
      if (event.run) startRun(event.run);
      else updateRun(event.run_id, { status: event.status ?? 'running' });
      break;
    case 'run_status_update':
      if (event.status) updateRun(event.run_id, { status: event.status });
      break;
    case 'step_complete':
      if (event.step) appendStepResult(event.run_id, event.step as unknown as StepResult);
      break;
    case 'run_complete':
      if (event.run) completeRun(event.run_id, event.run);
      break;
    case 'agent_status':
      if (event.agent_id && event.agent_status) {
        const state = store.getState();
        const agent = state.agents.get(event.agent_id);
        if (agent) updateAgent({ ...agent, status: event.agent_status as Agent['status'] });
      }
      break;
  }
}